"use client"

import { PSEUDO_ID_COOKIE } from "@/platform/api/constants"
import { catalogGateway } from "@/platform/api/gateways/catalog"
import type { StoreDiscount, StoreProduct } from "@/platform/api/store-contract"
import { getSessionCookie } from "@/platform/session/auth/get-session-cookie-client"
import { useGateway } from "@/platform/session/use-api"
import { usePrivateQuery } from "@/platform/session/use-private-query"
import { useSession } from "@/platform/session/use-session"
import { ANONYMOUS_VIEWER, viewerOf } from "@/platform/session/viewer"
import { getBrowserProductPricing, productLookup } from "@/platform/shopper/browser-product-pricing"

const emptyDiscounts: StoreDiscount[] = []

export const useBrowserProductPricing = (product: StoreProduct, regionId?: string, publicDiscounts?: StoreDiscount[], renderedFor?: string) => {
  const withDiscounts = publicDiscounts !== undefined
  const session = useSession()
  const catalog = useGateway(catalogGateway)
  const pseudoId = getSessionCookie(PSEUDO_ID_COOKIE)
  const rendered = renderedFor !== undefined && viewerOf(session.status) === renderedFor
  const foreign = renderedFor !== undefined && renderedFor !== ANONYMOUS_VIEWER && !rendered
  const personalizable = !!product.id && !!regionId && !rendered
  const allowed = personalizable && !session.isLoading
  const personalized = usePrivateQuery(
    {
      queryKey: ["browserProductPricing", product.id, regionId, product.handle, pseudoId, withDiscounts],
      enabled: allowed,
      queryFn: async () => {
        if (!regionId) throw new Error("Personalized pricing requires a region")
        const load = async (publicOnly: boolean) => {
          if (withDiscounts) return getBrowserProductPricing(catalog, product.id, regionId, product.handle, publicOnly)
          return { product: await catalog.product({ ...productLookup(product.id, product.handle), regionId, publicOnly }), discounts: emptyDiscounts }
        }
        try {
          return await load(false)
        } catch (error) {
          if (!foreign) throw error
          return load(true)
        }
      },
      staleTime: 60_000,
      retry: false,
    },
    true
  )
  const settled = !personalizable || (allowed && personalized.fetchStatus === "idle" && (!foreign || personalized.data !== undefined))
  const pricing = allowed && personalized.data ? personalized.data : { product, discounts: publicDiscounts ?? emptyDiscounts }
  return { ...pricing, settled }
}
